import {streamEmbedUrl, streamKey, distinctLiveStreams} from "../lib/streamEmbed";
import { twitchEmbedUrl } from "../lib/twitchEmbed";
import { useEffect, useState } from "react";
import api from "../api";
import { Tv, ExternalLink, Flame, Play } from "lucide-react";

export default function StreamsPage() {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStream, setActiveStream] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAllActiveStreams = async () => {
      try {
        const { data } = await api.get("/live-streams", { timeout: 15000 });
        if (cancelled) return;
        const allStreams = Array.isArray(data) ? data : [];

        const uniqueStreams = distinctLiveStreams(allStreams);

        setStreams(uniqueStreams);

        // احتفظ بالبث المختار إذا ما زال موجوداً.
        // إذا اختفى اختر أول بث متاح.
        setActiveStream((current) => {
          if (current) {
            const stillExists = uniqueStreams.find(
              (stream) => streamKey(stream) === streamKey(current)
            );

            if (stillExists) {
              return stillExists;
            }
          }

          return uniqueStreams.length > 0 ? uniqueStreams[0] : null;
        });
      } catch (err) {
        console.error("Failed to fetch streams:", err);

        if (!cancelled) {
          setStreams([]);
          setActiveStream(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchAllActiveStreams();

    const interval = setInterval(fetchAllActiveStreams, 60000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // توليد رابط الـ Embed حسب المنصة.
  const getEmbedUrl = (stream) => {
    if (!stream) return "";

    const hostname = window.location.hostname;
    const platform = (stream.platform || "").toLowerCase();

    if (platform.includes("twitch")) {
      return twitchEmbedUrl(stream, hostname);
    }

    if (platform.includes("youtube")) {
      let videoId = stream.video_id || "";

      if (!videoId && stream.url) {
        try {
          const parsed = new URL(stream.url);

          videoId =
            parsed.searchParams.get("v") ||
            parsed.pathname.split("/").filter(Boolean).pop() ||
            "";
        } catch {
          videoId = "";
        }
      }

      videoId = videoId || stream.username || "";

      return `https://www.youtube.com/embed/${encodeURIComponent(
        videoId
      )}?autoplay=1`;
    }

    if (platform.includes("kick")) {
      return streamEmbedUrl(stream, hostname);
    }

    return stream.url || "";
  };

  return (
    <div className="container py-8 space-y-8" dir="rtl">
      {/* رأس الصفحة */}
      <div className="bg-[#111113]/90 border border-white/10 rounded-3xl p-6 md:p-8 shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="space-y-2 text-center md:text-right">
          <div className="inline-flex items-center gap-2 bg-destructive/10 border border-destructive/30 px-3 py-1 rounded-full text-destructive text-xs font-black uppercase tracking-wider">
            <Flame size={14} />
            Rivals Live Hub
          </div>

          <h1 className="font-display font-black text-2xl md:text-4xl text-white">
            مشاهدة بثوث أبطال المنصة
          </h1>

          <p className="text-xs md:text-sm text-white/50 max-w-xl">
            تابع بثوث اللاعبين المحضّرين في كلاناتهم مباشرة من داخل المنصة دون
            الحاجة لمغادرة الموقع.
          </p>
        </div>

        <div className="bg-black/40 border border-white/10 px-5 py-4 rounded-2xl text-center min-w-[140px]">
          <div className="text-[10px] uppercase font-bold text-white/40 tracking-widest">
            بث مباشر الآن
          </div>

          <div className="font-display font-black text-3xl text-gold-500 mt-0.5">
            {streams.length}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-white/40 text-xs font-bold animate-pulse">
          جاري فحص وتحديث قنوات البث النشطة...
        </div>
      ) : streams.length === 0 ? (
        <div className="bg-[#111113]/90 border border-white/10 rounded-3xl p-16 text-center space-y-4 shadow-xl">
          <Tv className="mx-auto text-white/20" size={56} />

          <div className="text-base font-bold text-white/80">
            لا توجد بثوث مباشرة للمحضرين حالياً
          </div>

          <p className="text-xs text-white/40 max-w-md mx-auto">
            تأكد من التحضير في كلانك وبدء البث وربط حساب البث في ملفك الشخصي
            ليظهر بثك هنا عند تحديث القائمة.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* مشغل الفيديو الرئيسي */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#111113] border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
              <div className="relative aspect-video bg-black w-full">
                {activeStream ? (
                  <iframe
                    src={getEmbedUrl(activeStream)}
                    title={activeStream.username || "Live stream"}
                    className="w-full h-full border-0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center text-white/40 text-xs">
                    اختر بثاً من القائمة الجانبية للبدء بالمشاهدة
                  </div>
                )}
              </div>

              {/* معلومات البث الحالي */}
              {activeStream && (
                <div className="p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-white/5">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-destructive animate-pulse" />

                      <span className="font-bold text-white text-base">
                        {activeStream.username}
                      </span>

                      <span className="text-[10px] uppercase bg-black/60 px-2 py-0.5 rounded text-white/70 font-mono">
                        {activeStream.platform}
                      </span>
                    </div>

                    {activeStream.title && (
                      <p className="text-xs text-white/60">
                        {activeStream.title}
                      </p>
                    )}
                  </div>

                  {activeStream.url && (
                    <a
                      href={activeStream.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-2 rounded-xl text-white/80 hover:text-white transition"
                    >
                      <span>فتح في المنصة الأصلية</span>
                      <ExternalLink size={13} />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* قائمة البثوث */}
          <div className="space-y-4">
            <h3 className="font-display font-black text-white text-base flex items-center gap-2">
              <Tv size={18} className="text-gold-500" />
              البثوث النشطة ({streams.length})
            </h3>

            <div className="space-y-3 max-h-[600px] overflow-y-auto pl-1">
              {streams.map((stream) => {
                const isSelected =
                  streamKey(activeStream) === streamKey(stream);

                return (
                  <button
                    type="button"
                    key={streamKey(stream)}
                    onClick={() => setActiveStream(stream)}
                    className={`w-full text-right cursor-pointer bg-[#111113]/90 border rounded-2xl p-3 transition-all duration-300 flex items-center gap-3 group ${
                      isSelected
                        ? "border-gold-500 shadow-[0_0_15px_rgba(255,204,0,0.15)] bg-gold-500/5"
                        : "border-white/10 hover:border-white/30"
                    }`}
                  >
                    <div className="relative aspect-video w-28 bg-black rounded-xl overflow-hidden shrink-0">
                      {stream.thumbnail ? (
                        <img
                          src={stream.thumbnail}
                          alt={stream.username || "Live stream"}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-white/30 text-[10px]">
                          {stream.platform}
                        </div>
                      )}

                      <span className="absolute bottom-1 right-1 bg-destructive text-white text-[8px] font-black px-1.5 py-0.5 rounded">
                        LIVE
                      </span>
                    </div>

                    <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-bold text-xs text-white group-hover:text-gold-400 transition truncate">
                            {stream.username}
                          </span>

                          {isSelected && (
                            <Play
                              size={12}
                              className="text-gold-500 fill-gold-500 shrink-0"
                            />
                          )}
                        </div>

                        {stream.title && (
                          <p className="text-[11px] text-white/50 line-clamp-1">
                            {stream.title}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-between text-[10px] text-white/40 font-mono pt-2">
                        <span>{stream.platform}</span>
                        <span className="text-gold-500 font-bold">
                          شاهد الآن
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}